import * as XLSX from 'xlsx';
import { pdf, Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import { format } from 'date-fns';
import BahijLight from '../assets/fonts/Bahij-Badr-Light.ttf';
import BahijBold from '../assets/fonts/Bahij-Badr-Bold.ttf';

// Font registration for Pashto/Dari support using Bahij fonts
let fontRegistered = false;

// Register Bahij fonts with base64 encoding for proper Pashto/Dari support
export const registerFonts = async () => {
  if (fontRegistered) return;
  try {
    Font.register({
      family: 'BahijBadr',
      fonts: [
        { src: BahijLight, fontWeight: 'normal' },
        { src: BahijBold, fontWeight: 'bold' },
      ],
    });
    fontRegistered = true;
  } catch (e) {
    console.warn('Font registration warning:', e);
    fontRegistered = true;
  }
};

// Excel Export Functions with Perfect Pashto/Dari Support
export const generateDocumentsExcel = (filteredDocuments, selectedUsers, t) => {
  try {
    if (filteredDocuments.length === 0) {
      console.log('No documents to export');
      return;
    }

    // Prepare data for Excel
    const excelData = filteredDocuments.map((doc, index) => ({
      '#': index + 1,
      [t('common:reference')]: doc.reference_number || '-',
      [t('common:title')]: doc.title || '-',
      [t('common:type')]: doc.type || '-',
      [t('common:status')]: doc.status || '-',
      [t('common:creator')]: doc.creator?.full_name || doc.creator?.username || '-',
      [t('common:created_at')]: doc.created_at ? format(new Date(doc.created_at), 'dd/MM/yyyy') : '-'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 20 },  // Reference
      { wch: 40 },  // Title
      { wch: 15 },  // Type
      { wch: 15 },  // Status
      { wch: 25 },  // Creator
      { wch: 15 }   // Created At
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('limitedadmin:documents_report'));

    // Generate and download Excel file
    const fileName = `documents_report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
    XLSX.writeFile(wb, fileName);

    console.log('Documents Excel file generated successfully');
  } catch (error) {
    console.error('Error generating documents Excel:', error);
  }
};

export const generateTasksExcel = (filteredTasks, selectedUsers, t, getTaskStatusLabel) => {
  try {
    if (filteredTasks.length === 0) {
      console.log('No tasks to export');
      return;
    }

    // Prepare data for Excel
    const excelData = filteredTasks.map((task, index) => ({
      '#': index + 1,
      [t('common:reference')]: task.document?.reference_number || '-',
      [t('common:title')]: task.document?.title || '-',
      [t('my_assignments:assignee')]: task.assignee?.full_name || task.assignee?.username || '-',
      [t('my_assignments:assigned_by')]: task.assigner?.full_name || task.assigner?.username || '-',
      [t('my_assignments:status')]: getTaskStatusLabel(task.status),
      [t('documents:priority')]: task.priority || '-',
      [t('my_assignments:due_date')]: task.due_date ? format(new Date(task.due_date), 'dd/MM/yyyy') : '-',
      [t('my_assignments:assigned_at')]: task.assigned_at ? format(new Date(task.assigned_at), 'dd/MM/yyyy') : '-'
    }));

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    ws['!cols'] = [
      { wch: 5 },   // #
      { wch: 20 },  // Reference
      { wch: 35 },  // Title
      { wch: 25 },  // Assignee
      { wch: 25 },  // Assigned By
      { wch: 15 },  // Status
      { wch: 12 },  // Priority
      { wch: 15 },  // Due Date
      { wch: 15 }   // Assigned At
    ];

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, t('limitedadmin:tasks_report'));

    // Generate and download Excel file
    const fileName = `tasks_report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.xlsx`;
    XLSX.writeFile(wb, fileName);

    console.log('Tasks Excel file generated successfully');
  } catch (error) {
    console.error('Error generating tasks Excel:', error);
  }
};

// Professional PDF Export with proper Pashto/Dari support using @react-pdf/renderer
export const generateDocumentsPDF = async (filteredDocuments, selectedUserIds, selectedUsers, isRTL, t, allTaskAssignments = [], getTaskStatusLabel, getTaskStatusColor) => {
  try {
    // Ensure fonts are registered before generating PDF
    await registerFonts();
    
    console.log('=== PDF Generation Debug ===');
    console.log('Total documents to export:', filteredDocuments.length);
    console.log('First 5 documents:', filteredDocuments.slice(0, 5));
    console.log('All task assignments:', allTaskAssignments.length);
    
    if (filteredDocuments.length === 0) {
      console.log('No documents to export');
      return;
    }

    // Helper function to get document status (same as in LimitedAdminDashboard.jsx)
    const getDocumentStatus = (document) => {
      const taskAssignment = allTaskAssignments.find(ta => ta.document_id === document.id);
      const taskStatus = taskAssignment?.status || null;
      
      if (!taskStatus) {
        // Fallback to document status if no task assignment
        // Map DRAFT to Pashto text
        if (document.status === 'DRAFT') {
          return 'استول شوی ندی';
        }
        return document.status || '-';
      }
      
      // Only use valid task assignment statuses
      const validTaskStatuses = ['ASSIGNED', 'IN_PROGRESS', 'COMPLETED', 'REJECTED', 'FORWARDED', 'ACCEPTED', 'RECEIVED', 'REJECTED_RECEIPT'];
      if (!validTaskStatuses.includes(taskStatus)) {
        // Map DRAFT to Pashto text
        if (document.status === 'DRAFT') {
          return 'استول شوی ندی';
        }
        return document.status || '-';
      }
      
      // Return translated status label
      if (getTaskStatusLabel) {
        return getTaskStatusLabel(taskStatus);
      }
      return taskStatus;
    };

    // Create styles
    const styles = StyleSheet.create({
      page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 0,
        paddingRight: 0,
        fontFamily: 'BahijBadr',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left'
      },
      header: {
        backgroundColor: '#1976d2',
        padding: 15,
        alignItems: 'center',
        textAlign: isRTL ? 'right' : 'left'
      },
      brandRow: {
        backgroundColor: '#1976d2',
        paddingVertical: 12,
        paddingHorizontal: 20,
        marginBottom: 4,
        flexDirection: isRTL ? 'row-reverse' : 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
      },
      brandTitle: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: 'bold'
      },
      logo: {
        width: 28,
        height: 28
      },
      headerText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: isRTL ? 'right' : 'left'
      },
      metadata: {
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 15,
        paddingRight: 15,
        fontSize: 10,
        color: '#000000',
        textAlign: isRTL ? 'right' : 'left'
      },
      table: {
        display: 'table',
        width: 'auto',
        margin: '15px'
      },
      tableBody: {
        // extra top space so the fixed header does not overlap
        marginTop: 5
      },
      tableHeaderFixed: {
        // repeated on each page
        position: 'relative'
      },
      tableRow: {
        flexDirection: isRTL ? 'row-reverse' : 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#DDDDDD',
        borderBottomStyle: 'solid'
      },
      tableRowHeader: {
        backgroundColor: '#1976d2',
        flexDirection: isRTL ? 'row-reverse' : 'row'
      },
      tableCol: {
        padding: 2,
        fontSize: 8
      },
      tableColHeader: {
        backgroundColor: '#1976d2',
        padding: 2,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#FFFFFF'
      },
      tableCell: {
        margin: 0,
        fontSize: 8,
        color: '#000000',
        textAlign: isRTL ? 'right' : 'left'
      },
      tableCellHeader: {
        margin: 0,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#FFFFFF',
        textAlign: isRTL ? 'right' : 'left'
      },
      alternateRow: {
        backgroundColor: '#F5F5F5'
      },
      footer: {
        position: 'absolute',
        bottom: 30,
        left: 15,
        right: 15,
        fontSize: 8,
        color: '#808080',
        flexDirection: 'row',
        justifyContent: isRTL ? 'flex-end' : 'space-between',
        textAlign: isRTL ? 'right' : 'left'
      },
      closingLine: {
        marginTop: 20,
        marginBottom: 10,
        paddingRight: 20,
        fontSize: 9,
        color: '#606060',
        textAlign: isRTL ? 'right' : 'left'
      }
    });

    // Get user names
    const getUserNames = () => {
      if (selectedUserIds.length === 0) return '';
      return selectedUsers.map(u => u.full_name || u.username).join(', ');
    };

    // Create PDF Document
    const DocComponent = () => (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          {/* Branded Header (first page only) */}
          <View style={styles.header}>
            <Text style={styles.headerText}>{t('limitedadmin:documents_report')}</Text>
          </View>

          {/* Metadata */}
          <View style={styles.metadata}>
            <Text>{`${t('common:generated_on')}: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`}</Text>
            {selectedUserIds.length > 0 && (
              <Text style={{ marginTop: 5 }}>
                {`${t('limitedadmin:for_users')}: ${getUserNames()}`}
              </Text>
            )}
            <Text style={{ marginTop: 5, fontWeight: 'bold' }}>
              {`${t('limitedadmin:total_documents')}: ${filteredDocuments.length}`}
            </Text>
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Table Header (repeats each page) */}
            <View fixed style={[styles.tableRow, styles.tableRowHeader, styles.tableHeaderFixed]}>
              <View style={[styles.tableColHeader, { width: '5%' }]}>
                <Text style={styles.tableCellHeader}>#</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:reference')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '25%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:title')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '12%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:type')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '12%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:status')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '16%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:creator')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '15%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:created_at')}</Text>
              </View>
            </View>

            {/* Table Body */}
            {filteredDocuments.map((document, index) => (
              <View 
                key={index} 
                style={[
                  styles.tableRow,
                  styles.tableBody,
                  index % 2 === 1 && styles.alternateRow
                ]}
              >
                <View style={[styles.tableCol, { width: '5%' }]}>
                  <Text style={[styles.tableCell, { textAlign: 'center' }]}>{index + 1}</Text>
                </View>
                <View style={[styles.tableCol, { width: '15%' }]}>
                  <Text style={styles.tableCell}>{document.reference_number || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '25%' }]}>
                  <Text style={styles.tableCell}>{document.title || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '12%' }]}>
                  <Text style={styles.tableCell}>{document.type || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '12%' }]}>
                  <Text style={styles.tableCell}>{getDocumentStatus(document)}</Text>
                </View>
                <View style={[styles.tableCol, { width: '16%' }]}>
                  <Text style={styles.tableCell}>
                    {document.creator?.full_name || document.creator?.username || '-'}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: '15%' }]}>
                  <Text style={styles.tableCell}>
                    {document.created_at ? format(new Date(document.created_at), 'dd/MM/yyyy') : '-'}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          {/* Closing line only on last page under the table */}
          <Text style={styles.closingLine} render={({ pageNumber, totalPages }) => (
            pageNumber === totalPages 
              ? `${t('common:page') ?? 'Page'} ${pageNumber}/${totalPages}  |  ${t('limitedadmin:total_documents')}: ${filteredDocuments.length}` 
              : ''
          )} />
        </Page>
      </Document>
    );

    // Generate PDF blob
    const blob = await pdf(<DocComponent />).toBlob();
    
    // Download PDF
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `documents_report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('Documents PDF generated successfully');
  } catch (error) {
    console.error('Error generating documents PDF:', error);
  }
};

export const generateTasksPDF = async (filteredTasks, selectedUserIds, selectedUsers, taskViewMode, isRTL, t, getTaskStatusLabel) => {
  try {
    // Ensure fonts are registered before generating PDF
    await registerFonts();
    
    console.log('=== PDF Generation Debug (Tasks) ===');
    console.log('Total tasks to export:', filteredTasks.length);
    console.log('First 5 tasks:', filteredTasks.slice(0, 5));
    
    if (filteredTasks.length === 0) {
      console.log('No tasks to export');
      return;
    }

    // Create styles
    const styles = StyleSheet.create({
      page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        paddingTop: 10,
        paddingBottom: 10,
        paddingLeft: 0,
        paddingRight: 0,
        fontFamily: 'BahijBadr',
        direction: isRTL ? 'rtl' : 'ltr',
        textAlign: isRTL ? 'right' : 'left'
      },
      header: {
        backgroundColor: '#1976d2',
        padding: 15,
        alignItems: 'center',
        textAlign: isRTL ? 'right' : 'left'
      },
      headerText: {
        color: '#FFFFFF',
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: isRTL ? 'right' : 'left'
      },
      metadata: {
        paddingTop: 2,
        paddingBottom: 2,
        paddingLeft: 15,
        paddingRight: 15,
        fontSize: 10,
        color: '#000000',
        textAlign: isRTL ? 'right' : 'left'
      },
      table: {
        display: 'table',
        width: 'auto',
        margin: '15px'
      },
      tableBody: {
        // extra top space so the fixed header does not overlap
        marginTop: 5
      },
      tableHeaderFixed: {
        // repeated on each page
        position: 'relative'
      },
      tableRow: {
        flexDirection: isRTL ? 'row-reverse' : 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#DDDDDD',
        borderBottomStyle: 'solid'
      },
      tableRowHeader: {
        backgroundColor: '#1976d2',
        flexDirection: isRTL ? 'row-reverse' : 'row'
      },
      tableCol: {
        padding: 2,
        fontSize: 8
      },
      tableColHeader: {
        backgroundColor: '#1976d2',
        padding: 2,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#FFFFFF'
      },
      tableCell: {
        margin: 0,
        fontSize: 8,
        color: '#000000',
        textAlign: isRTL ? 'right' : 'left'
      },
      tableCellHeader: {
        margin: 0,
        fontSize: 8,
        fontWeight: 'bold',
        color: '#FFFFFF',
        textAlign: isRTL ? 'right' : 'left'
      },
      alternateRow: {
        backgroundColor: '#F5F5F5'
      },
      footer: {
        position: 'absolute',
        bottom: 30,
        left: 15,
        right: 15,
        fontSize: 8,
        color: '#808080',
        flexDirection: 'row',
        justifyContent: isRTL ? 'flex-end' : 'space-between',
        textAlign: isRTL ? 'right' : 'left'
      },
      closingLine: {
        marginTop: 20,
        marginBottom: 10,
        paddingRight: 20,
        fontSize: 9,
        color: '#606060',
        textAlign: isRTL ? 'right' : 'left'
      }
    });

    // Get user names
    const getUserNames = () => {
      if (selectedUserIds.length === 0) return '';
      return selectedUsers.map(u => u.full_name || u.username).join(', ');
    };

    // Create PDF Document
    const DocComponent = () => (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          {/* Branded Header (first page only) */}
          <View style={styles.header}>
            <Text style={styles.headerText}>{t('limitedadmin:tasks_report')}</Text>
          </View>

          {/* Metadata */}
          <View style={styles.metadata}>
            <Text>{`${t('common:generated_on')}: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`}</Text>
            {selectedUserIds.length > 0 && (
              <Text style={{ marginTop: 5 }}>
                {`${t('limitedadmin:for_users')}: ${getUserNames()}`}
              </Text>
            )}
            <Text style={{ marginTop: 5, fontWeight: 'bold' }}>
              {`${t('limitedadmin:total_tasks')}: ${filteredTasks.length}`}
            </Text>
          </View>

          {/* Table */}
          <View style={styles.table}>
            {/* Table Header (repeats each page) */}
            <View fixed style={[styles.tableRow, styles.tableRowHeader, styles.tableHeaderFixed]}>
              <View style={[styles.tableColHeader, { width: '4%' }]}>
                <Text style={styles.tableCellHeader}>#</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '12%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:reference')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '24%' }]}>
                <Text style={styles.tableCellHeader}>{t('common:title')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '16%' }]}>
                <Text style={styles.tableCellHeader}>{t('my_assignments:assignee')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '16%' }]}>
                <Text style={styles.tableCellHeader}>{t('my_assignments:assigned_by')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '12%' }]}>
                <Text style={styles.tableCellHeader}>{t('my_assignments:status')}</Text>
              </View>
              <View style={[styles.tableColHeader, { width: '16%' }]}>
                <Text style={styles.tableCellHeader}>{t('my_assignments:assigned_at')}</Text>
              </View>
            </View>

            {/* Table Body */}
            {filteredTasks.map((task, index) => (
              <View 
                key={index} 
                style={[
                  styles.tableRow,
                  styles.tableBody,
                  index % 2 === 1 && styles.alternateRow
                ]}
              >
                <View style={[styles.tableCol, { width: '4%' }]}>
                  <Text style={[styles.tableCell, { textAlign: 'center' }]}>{index + 1}</Text>
                </View>
                <View style={[styles.tableCol, { width: '12%' }]}>
                  <Text style={styles.tableCell}>{task.document?.reference_number || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '24%' }]}>
                  <Text style={styles.tableCell}>{task.document?.title || '-'}</Text>
                </View>
                <View style={[styles.tableCol, { width: '16%' }]}>
                  <Text style={styles.tableCell}>
                    {task.assignee?.full_name || task.assignee?.username || '-'}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: '16%' }]}>
                  <Text style={styles.tableCell}>
                    {task.assigner?.full_name || task.assigner?.username || '-'}
                  </Text>
                </View>
                <View style={[styles.tableCol, { width: '12%' }]}>
                  <Text style={styles.tableCell}>{getTaskStatusLabel(task.status)}</Text>
                </View>
                <View style={[styles.tableCol, { width: '16%' }]}>
                  <Text style={styles.tableCell}>
                    {task.assigned_at ? format(new Date(task.assigned_at), 'dd/MM/yyyy') : '-'}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          {/* Closing line only on last page under the table */}
          <Text style={styles.closingLine} render={({ pageNumber, totalPages }) => (
            pageNumber === totalPages 
              ? `${t('common:page') ?? 'Page'} ${pageNumber}/${totalPages}  |  ${t('limitedadmin:total_tasks')}: ${filteredTasks.length}` 
              : ''
          )} />
        </Page>
      </Document>
    );

    // Generate PDF blob
    const blob = await pdf(<DocComponent />).toBlob();
    
    // Download PDF
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tasks_report_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log('Tasks PDF generated successfully');
  } catch (error) {
    console.error('Error generating tasks PDF:', error);
  }
};

